import { Blockquote } from '@/components/ui/blockquote';
import { Box, Heading, HStack, List, Mark, Text } from '@chakra-ui/react';
import type { ProgramDeletionCheckResponse } from '@fluenceur/types';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XmarkCircle } from 'iconoir-react';

interface DeletionImpactSummaryProps {
  checkData: ProgramDeletionCheckResponse;
}

const DeletionImpactSummary: React.FC<DeletionImpactSummaryProps> = ({ checkData }) => {
  const { t } = useTranslation();
  const { stats } = checkData;
  const hasActiveData =
    stats.activeAffiliates > 0 || stats.unpaidCommissions > 0 || stats.recentOrders > 0;

  return (
    <Blockquote colorPalette="gray" variant="plain" bg="gray.50" rounded="md" p={5}>
      <HStack gap={2} mb={3}>
        <Heading as="h4" size="md">
          {t('programs.deletion.impactSummary.title')}
        </Heading>
      </HStack>

      <List.Root gap={2} variant="plain" align="center" fontSize="sm" fontWeight="normal">
        {/* What remains accessible */}
        <List.Item>
          <List.Indicator asChild color="green.500" boxSize={4}>
            <CheckCircle />
          </List.Indicator>
          <Text>{t('programs.deletion.impactSummary.historicalDataRemains')}</Text>
        </List.Item>

        {stats.unpaidCommissions > 0 && (
          <List.Item>
            <List.Indicator asChild color="green.500">
              <CheckCircle />
            </List.Indicator>
            {t('programs.deletion.impactSummary.payPendingCommissions', {
              count: stats.unpaidCommissions,
            }).replace(
              stats.unpaidCommissions.toString(),
              `<mark>${stats.unpaidCommissions}</mark>`
            ) ? (
              <Text
                dangerouslySetInnerHTML={{
                  __html: t('programs.deletion.impactSummary.payPendingCommissions', {
                    count: stats.unpaidCommissions,
                  }).replace(
                    stats.unpaidCommissions.toString(),
                    `<mark style="color: #16a34a; background: none; font-weight: 600;">${stats.unpaidCommissions}</mark>`
                  ),
                }}
              />
            ) : null}
          </List.Item>
        )}

        {/* What stops working */}
        <List.Item>
          <List.Indicator asChild color="red.500" boxSize={4}>
            <XmarkCircle />
          </List.Indicator>
          {t('programs.deletion.impactSummary.noNewOrders')}
        </List.Item>

        {stats.activeAffiliates > 0 && (
          <List.Item>
            <List.Indicator asChild color="red.500" boxSize={4}>
              <XmarkCircle />
            </List.Indicator>
            {t('programs.deletion.impactSummary.affiliatesLoseAccess', {
              count: stats.activeAffiliates,
            })}
          </List.Item>
        )}

        <List.Item>
          <List.Indicator asChild color="red.500" boxSize={4}>
            <XmarkCircle />
          </List.Indicator>
          {t('programs.deletion.impactSummary.discountCodesStop')}
        </List.Item>
      </List.Root>

      {/* Helpful tip if there are unpaid commissions */}
      {stats.unpaidCommissions > 0 && (
        <Box mt={3} p={3} bg="blue.subtle" rounded="md">
          <Text fontSize="sm" color="blue.fg">
            {t('programs.deletion.impactSummary.tipPayCommissions', {
              count: stats.unpaidCommissions,
            })}
          </Text>
        </Box>
      )}

      {/* If there's no active data, show a simpler message */}
      {!hasActiveData && (
        <Box mt={3} p={3} bg="green.subtle" rounded="md">
          <Text fontSize="sm" color="green.fg">
            {t('programs.deletion.impactSummary.safeToDelete')}
          </Text>
        </Box>
      )}
    </Blockquote>
  );
};

export default DeletionImpactSummary;
